/**
 * PS-6495 — ordering a SIP-Account from the Connect page via `POST /bookingmgmt/v2/outbox`
 * (`orderSipAccount`). Shapes taken from that endpoint's OpenAPI schema, probed on test
 * 2026-09-03 (bookingmgmt-api 2.0.220-SNAPSHOT), not from the ticket text.
 */

/**
 * The carrier every SIP-Account number is expected to be booked on. A DB-managed value, so it
 * lives as a string constant rather than an enum (global rule #5) — the backend resolves it from
 * the number block, we only assert it in tests and log it.
 */
export const OUTBOX_CARRIER = 'OUTBOX';

/**
 * The values `result` can take, copied from `OutboxOrderResponse` in the bookingmgmt repo
 * (`lib-bookingmgmt/.../outbox/OutboxOrderResponse.java`, branch
 * `PS-6497-update-booking-of-cc-numbers`, read 2026-09-04).
 *
 * **`result` is NOT a confirm/reject flag.** Its own Javadoc: "distinguishes a fresh booking from
 * an idempotent hit, so a caller does not have to infer it by comparing `orderedAt` against its own
 * clock." Both values are SUCCESSES, and both come back through the same `toResponse(...)`, which
 * always fills `number`, `phoneNumberId` and `carrier`.
 *
 * So branching on `result === ORDERED` would show the apology screen to a customer whose account
 * already exists — a false failure on a working path. There is no rejection value at all: a refusal
 * throws in `OutboxOrderService` and arrives as an HTTP error.
 */
export const OUTBOX_RESULT_ORDERED = 'ORDERED';
export const OUTBOX_RESULT_ALREADY_EXISTS = 'ALREADY_EXISTS';

/**
 * `java.time.LocalDate` as the endpoint's schema documents it: the generator expands the class
 * through its getters instead of emitting a date string. Only the three fields that actually
 * identify a day are sent; the schema's derived members (`dayOfWeek`, `dayOfYear`, `era`,
 * `leapYear`, `month`, `chronology`) are computed by Jackson on the receiving side.
 */
export interface LocalDateBean {
	year: number;
	monthValue: number;
	dayOfMonth: number;
}

/**
 * Which wire form `startDate` takes.
 *
 * The schema documents `localDateBean`. Spring most likely also accepts a plain `YYYY-MM-DD`
 * string, because that is how it deserialises `LocalDate` by default — but that is **unverified**:
 * a POST to this endpoint books a real, billed carrier number, so it was deliberately not probed.
 * Resolve it in TC3 against a throwaway phone service, or ask the endpoint's author, then set
 * {@link START_DATE_FORM} accordingly. This type plus that constant are the only places that
 * need to change.
 */
export type StartDateForm = 'iso' | 'localDateBean';

/** The form we send until the accepted one is confirmed. See {@link StartDateForm}. */
export const START_DATE_FORM: StartDateForm = 'iso';

export type SipAccountOrderStartDate = string | LocalDateBean;

export interface SipAccountOrderRequest {
	phoneServiceId: number;
	startDate: SipAccountOrderStartDate;
}

/** Where an order goes: `/outbox` with the customer's Bearer, or `/outbox/linkorders` with the link uid and no Bearer. */
export type SipOrderTarget =
	| { kind: 'phoneService'; phoneServiceId: number }
	| { kind: 'link'; token: string };

export interface SipAccountOrderResponse {
	/** Expected to equal {@link OUTBOX_CARRIER}. */
	carrier: string;
	/** The booked number, in international format. */
	number: string;
	phoneNumberId: number;
	phoneServiceId: number;
	/** Backend status string. Recorded and surfaced on failure; not interpreted by the UI. */
	result: string;
	/**
	 * Serialized `java.time.LocalDateTime`. Deliberately untyped: the UI does not consume it, and
	 * inventing a shape for it would invite someone to rely on fields nobody verified.
	 */
	orderedAt?: unknown;
}

/**
 * Renders a calendar date in the form the endpoint expects.
 *
 * Uses the **local** calendar fields on purpose. `toISOString()` converts to UTC first, so a local
 * midnight in any positive offset — Berlin always is — would report the previous day and book the
 * account one day early.
 */
export function toStartDate(
	date: Date,
	form: StartDateForm = START_DATE_FORM,
): SipAccountOrderStartDate {
	const year = date.getFullYear();
	const monthValue = date.getMonth() + 1;
	const dayOfMonth = date.getDate();

	if (form === 'localDateBean') {
		return { year, monthValue, dayOfMonth };
	}

	const pad = (value: number): string => String(value).padStart(2, '0');
	return `${year}-${pad(monthValue)}-${pad(dayOfMonth)}`;
}

/**
 * `errorCode` values bookingmgmt puts in the body of a refused order. String constants, not an
 * enum: the backend owns the vocabulary and may add codes.
 *
 * `ADDRESS_INVALID` (HTTP 422): the contract address could not be used, nothing was booked.
 * `LOGINNAME_TAKEN` (HTTP 409): the SIP login is taken. It is listed for the contract only - the
 * screen treats it like any other refusal.
 *
 * @ticket PS-6605
 */
export const ORDER_ERROR_ADDRESS_INVALID = 'ADDRESS_INVALID';
export const ORDER_ERROR_LOGINNAME_TAKEN = 'LOGINNAME_TAKEN';
